import LegalPage, { LegalSection } from "./LegalPage";
import { useContent } from "../lib/contentContext";

export default function Terms() {
    const { content } = useContent();
    const biz = content.legal.business_name;
    const owner = content.legal.owner;
    const jurisdiction = content.legal.jurisdiction;
    const email = content.contact.email;
    const mail = (
        <a href={`mailto:${email}`} className="link-underline text-gold">
            {email}
        </a>
    );

    return (
        <LegalPage
            eyebrow="Legal"
            title="Terms of Service"
            updated={content.legal.effective_date}
        >
            <p>
                Welcome to {biz}. These Terms of Service ("Terms") govern your
                access to and use of our website, communications, and services.
                By using the site, requesting a consultation, or receiving
                services, you agree to these Terms and to our{" "}
                <a href="/privacy" className="link-underline text-gold">
                    Privacy Policy
                </a>
                . If you do not agree, please do not use the site or our
                services.
            </p>

            <LegalSection title="Eligibility">
                <p>
                    You must be at least 18 years of age to use this site,
                    request services, or enter into any agreement with us.
                    Certain treatments may be provided to minors only with the
                    consent and in-person involvement of a parent or legal
                    guardian, and at our professional discretion.
                </p>
            </LegalSection>

            <LegalSection title="Medical disclaimer — no doctor–patient relationship online">
                <p>
                    Content on this website is for general informational purposes
                    only and is not medical advice. Viewing the site or
                    submitting a request does not create a provider–patient
                    relationship. That relationship is established only through an
                    in-person or telehealth evaluation and mutual agreement to
                    proceed. Always seek the advice of a qualified health provider
                    with any questions about a medical condition.
                </p>
            </LegalSection>

            <LegalSection title="Services & consultation">
                <p>
                    Complimentary consultations are informational and are not a
                    substitute for a full medical evaluation. Treatment decisions
                    are made on a case-by-case basis by {owner} and are subject to
                    medical suitability. We reserve the right to decline or
                    discontinue services when, in our professional judgment, a
                    treatment is not appropriate or safe.
                </p>
            </LegalSection>

            <LegalSection title="Appointments, deposits, cancellations & no-shows">
                <p>
                    Please provide at least 24 hours' notice to reschedule or
                    cancel an appointment. Late arrivals may result in a shortened
                    or rescheduled visit. Repeated late cancellations or no-shows
                    may require a non-refundable deposit to secure future
                    bookings. Any deposit, cancellation, or late-fee policy for a
                    specific treatment will be communicated to you at the time of
                    booking. Appointments booked through our third-party
                    scheduling/EMR system are also subject to that provider's
                    terms.
                </p>
            </LegalSection>

            <LegalSection title="Pricing, payment & financing">
                <p>
                    Prices displayed are "starting-at" prices and are subject to
                    change. Final pricing is provided in your personalized
                    treatment plan following consultation. Promotional pricing is
                    time-limited and cannot be combined with other offers unless
                    expressly stated. Payment is due at the time of service unless
                    otherwise agreed.
                </p>
                <p>
                    We offer optional financing through third-party partners
                    (such as Cherry). Financing applications, approvals, rates,
                    and repayment are governed solely by the financing provider's
                    terms and are not controlled by us. We are not a lender and
                    make no representations regarding approval or terms.
                </p>
            </LegalSection>

            <LegalSection title="Results & informed consent">
                <p>
                    Individual results vary and are not guaranteed. Aesthetic and
                    wellness treatments carry inherent risks, benefits, and
                    limitations that will be discussed with you before any
                    procedure. You will be asked to review and sign
                    treatment-specific informed-consent documents. Any "before and
                    after" images shown reflect individual experiences and are not
                    a promise of your results.
                </p>
            </LegalSection>

            <LegalSection title="Client responsibilities">
                <p>
                    You agree to provide accurate and complete personal and
                    medical information, disclose medications and conditions, and
                    follow all pre- and post-treatment instructions. Failure to do
                    so may affect your safety and outcomes and may limit our
                    liability.
                </p>
            </LegalSection>

            <LegalSection title="Communications & text messaging">
                <p>
                    By providing your contact information, you consent to receive
                    appointment-related communications from us. With your separate
                    opt-in, we may send promotional messages by email or text
                    (SMS/MMS); message and data rates may apply and you can opt
                    out of marketing messages at any time (reply STOP to texts).
                    See our Privacy Policy for details.
                </p>
            </LegalSection>

            <LegalSection title="Third-party links & tools">
                <p>
                    Our website and communications may link to or embed
                    third-party services (for example, scheduling/EMR systems,
                    financing providers, and social media). We are not responsible
                    for the content, availability, or practices of those third
                    parties, and your use of them is governed by their terms.
                </p>
            </LegalSection>

            <LegalSection title="Acceptable use">
                <p>
                    You agree not to misuse the site, including attempting to gain
                    unauthorized access, interfering with its operation, uploading
                    malicious code, or using it for any unlawful purpose.
                </p>
            </LegalSection>

            <LegalSection title="Intellectual property">
                <p>
                    All content on this site — including text, imagery, layout,
                    and the {biz} brand and logos — is owned by {biz} or its
                    licensors and may not be copied, reproduced, or distributed
                    without our prior written permission.
                </p>
            </LegalSection>

            <LegalSection title="Disclaimer & limitation of liability">
                <p>
                    The website and its content are provided "as is" without
                    warranties of any kind. To the fullest extent permitted by
                    law, {biz} disclaims all implied warranties and shall not be
                    liable for any indirect, incidental, special, or consequential
                    damages arising from your use of the site or services. Nothing
                    in these Terms limits liability that cannot be limited under
                    applicable law.
                </p>
            </LegalSection>

            <LegalSection title="Indemnification">
                <p>
                    You agree to indemnify and hold harmless {biz}, its owner, and
                    staff from any claims, damages, or expenses arising out of your
                    breach of these Terms or your misuse of the site or services.
                </p>
            </LegalSection>

            <LegalSection title="Governing law & dispute resolution">
                <p>
                    These Terms are governed by the laws of {jurisdiction}, without
                    regard to conflict-of-law principles. Any dispute will be
                    resolved in the courts or forum designated by {biz} to the
                    extent permitted by applicable law. If any provision is found
                    unenforceable, the remaining provisions remain in effect.
                </p>
            </LegalSection>

            <LegalSection title="Changes to these Terms">
                <p>
                    We may update these Terms from time to time. The current
                    version, with its effective date, will always be posted on
                    this page, and continued use of the site constitutes
                    acceptance of the updated Terms.
                </p>
            </LegalSection>

            <LegalSection title="Contact">
                <p>Questions about these Terms? Contact {biz} at {mail}.</p>
                <p className="text-sm text-white/40">
                    These Terms are provided as a general template for
                    informational purposes and do not constitute legal advice. We
                    recommend review by qualified legal counsel.
                </p>
            </LegalSection>
        </LegalPage>
    );
}
